import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, ArrowLeft, CheckCircle } from 'lucide-react';

const About: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">HomeGrowInvest</h1>
                <p className="text-sm text-gray-500">Financial Planner</p>
              </div>
            </Link>
            <div className="flex items-center space-x-3">
              <Link
                to="/login"
                className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
              >
                Login
              </Link>
              <Link
                to="/login?register=true"
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link
          to="/"
          className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">About HomeGrowInvest</h2>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">What is HomeGrowInvest?</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              HomeGrowInvest is a comprehensive financial planning platform designed to help families manage and track
              all their investments in one secure, easy-to-use interface. Whether you're managing Mutual Funds, Stocks,
              Bonds, Fixed Deposits, or other investment vehicles, HomeGrowInvest provides a centralized view of your
              entire financial portfolio.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Our platform simplifies the complexity of managing multiple investment accounts and provides clear insights
              into your family's financial health, helping you make informed decisions for your future.
            </p>
          </section>

          <section className="mb-8">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-amber-900 mb-3">Important Disclaimer</h3>
              <p className="text-amber-800 leading-relaxed">
                HomeGrowInvest is a portfolio management and tracking tool only. We <strong>do not support investing</strong> through our platform, 
                and we <strong>do not recommend</strong> specific funds or equities for purchase. Our platform simply helps you manage and 
                visualize the growth of your existing investments over time. All investment decisions should be made in consultation 
                with qualified financial advisors based on your individual financial situation and risk tolerance.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Key Features</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-primary-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <strong className="text-gray-900">Comprehensive Investment Tracking:</strong>
                  <span className="text-gray-700"> Track all types of investments including Mutual Funds, Indian Stocks, US Stocks, Corporate Bonds, Government Bonds, SGB, Fixed Deposits, and more.</span>
                </div>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-primary-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <strong className="text-gray-900">Family Portfolio Management:</strong>
                  <span className="text-gray-700"> Manage investments for multiple family members and get a consolidated view of your family's financial portfolio.</span>
                </div>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-primary-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <strong className="text-gray-900">CAS PDF Import:</strong>
                  <span className="text-gray-700"> Automatically import your holdings from Consolidated Account Statement (CAS) PDF files. Simply upload your CAS PDF and let HomeGrowInvest extract all your investments automatically.</span>
                </div>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-primary-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <strong className="text-gray-900">Expense & Income Tracking:</strong>
                  <span className="text-gray-700"> Track recurring and one-time expenses, manage income sources, and calculate your monthly surplus for better financial planning.</span>
                </div>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-primary-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <strong className="text-gray-900">Financial Analytics:</strong>
                  <span className="text-gray-700"> Visualize your investment performance with interactive charts, understand your asset allocation, and track growth across different investment types.</span>
                </div>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-primary-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <strong className="text-gray-900">30-Year Financial Model:</strong>
                  <span className="text-gray-700"> Project your financial future with detailed 30-year projections based on your current investments and savings.</span>
                </div>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-primary-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <strong className="text-gray-900">Secure & Private:</strong>
                  <span className="text-gray-700"> Your financial data is encrypted and stored securely. Each user's data is completely isolated, ensuring maximum privacy and security.</span>
                </div>
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">How to Use HomeGrowInvest</h3>
            
            <div className="space-y-6">
              <div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">1. Create Your Account</h4>
                <p className="text-gray-700">
                  Sign up for a free account with your email address. Your account is secure and private.
                </p>
              </div>

              <div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">2. Add Family Members</h4>
                <p className="text-gray-700">
                  Add yourself and other family members to track investments for each person separately. This helps you
                  organize your portfolio and understand investments by family member.
                </p>
              </div>

              <div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">3. Import or Add Investments</h4>
                <p className="text-gray-700 mb-2">
                  There are two ways to add your investments:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                  <li><strong>CAS PDF Import:</strong> If you have a Consolidated Account Statement (CAS) PDF from CDSL or NSDL, use the CAS Upload feature to automatically import all your holdings.</li>
                  <li><strong>Manual Entry:</strong> Add investments manually through the Investments page, providing details like investment type, amount, current value, and other relevant information.</li>
                </ul>
              </div>

              <div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">4. Track Expenses and Income</h4>
                <p className="text-gray-700">
                  Add your recurring expenses and income sources to get a complete picture of your monthly cash flow.
                  This helps you understand how much you can invest each month.
                </p>
              </div>

              <div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">5. Analyze and Plan</h4>
                <p className="text-gray-700">
                  Use the Overview page to see your portfolio at a glance, view charts showing your asset allocation,
                  and use the Financial Model to project your future financial position.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Security & Privacy</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We take your financial data security seriously. All data is encrypted and stored securely. Each user's
              data is completely isolated - you can only access your own information. We use industry-standard
              authentication and authorization to protect your account.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Your financial information is never shared with third parties, and we don't use your data for any
              commercial purposes beyond providing you with the HomeGrowInvest service.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Get Started Today</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Ready to take control of your family's financial future? Sign up for free and start managing your
              investments with HomeGrowInvest today.
            </p>
            <Link
              to="/login?register=true"
              className="inline-block px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-semibold"
            >
              Create Your Account
            </Link>
          </section>
        </div>
      </div>
    </div>
  );
};

export default About;

